import { get, isEmpty } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import {
  getReservationsByProductType,
  processPassengerType,
} from 'utils/helpers';
import { getPassengerTypes } from '../types';

export const getCurrentBooking = (state) => state.booking?.current || {};

export const isBookingOneWay = createSelector(
  getCurrentBooking,
  (booking) =>
    get(
      booking,
      ['reservations_attributes', 0, 'inverse_reservation_id'],
      false // if we can't access this path, we dont want to assume its one way
    ) === null
);

export const getAmountToPay = createSelector(
  getCurrentBooking,
  (state) => state.checkout.paymentAmount,
  (booking, paymentAmount) => {
    if (paymentAmount === 'balance') {
      return booking.balance_in_cents || 0;
    }
    return booking.deposit_outstanding_in_cents || 0;
  }
);

export const getCommissionAmount = createSelector(
  getCurrentBooking,
  (booking) => booking.commission_in_cents
);

export const getBookingPaymentTypes = createSelector(
  getCurrentBooking,
  (booking) => booking.payment_types_attributes || []
);

export const getPayPalPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) =>
        paymentType.name === 'PayPal' && paymentType.gateway === 'braintree'
    )
);

export const getPaymentTypeId = (state) => state.checkout.paymentTypeId;

export const getSurchargeInCents = createSelector(
  getCurrentBooking,
  (booking) => booking.surcharge_in_cents
);

export const getSelectedPaymentTypes = createSelector(
  getBookingPaymentTypes,
  getPaymentTypeId,
  (bookingPaymentTypes, paymentTypeId) =>
    bookingPaymentTypes.find(
      (paymentType) => paymentType.id === paymentTypeId
    ) || {}
);

export const getTransactionFee = createSelector(
  getSelectedPaymentTypes,
  (selectedPaymentType) => Number(selectedPaymentType?.transaction_fee || 0)
);

export const getSurchargeAmount = createSelector(
  getAmountToPay,
  getSurchargeInCents,
  getTransactionFee,
  (amountToPay, surchargeInCents, transactionFee) => {
    let surchargeAmount = 0;
    if (transactionFee !== 0) {
      surchargeAmount = amountToPay * (transactionFee / 100);
    } else if (surchargeInCents) {
      surchargeAmount = surchargeInCents;
    }
    return surchargeAmount;
  }
);

/*
  Steps to get prices for each passenger types and vehicles
    1. Get reservation ids
    2. Find each reservation in the reservations_attributes
    3. Sum up each passenger and vehicles
*/
export const getPrices = createSelector(
  getCurrentBooking,
  getPassengerTypes,
  (currentBooking, qtPassengerTypes) => {
    const passengerPrices = {};
    let vehiclePrice = 0;
    let commissionPrice = 0;
    let ferrySubtotalPrice = 0;

    if (!isEmpty(currentBooking)) {
      // Find each reservation in the reservations_attributes
      const ferryReservations = getReservationsByProductType(
        'ferry,transfer',
        currentBooking
      );

      // Sum up each passengers and vehicles
      ferryReservations.forEach((reservation) => {
        const passengerDetail = processPassengerType(
          reservation.passenger_splits,
          currentBooking.passengers_attributes,
          qtPassengerTypes
        );

        Object.keys(passengerDetail).forEach((key) => {
          const pricePerPassenger =
            reservation.consumer_grosses[passengerDetail[key].consumer_id];

          if (passengerPrices[key]) {
            passengerPrices[key] +=
              pricePerPassenger * passengerDetail[key].count;
          } else {
            passengerPrices[key] =
              pricePerPassenger * passengerDetail[key].count;
          }
        });

        reservation.vehicle_splits.forEach((vehicle) => {
          vehiclePrice += reservation.consumer_grosses[vehicle.consumer_id];
        });

        commissionPrice += reservation.pre_adjusted_commission_in_cents;

        ferrySubtotalPrice += reservation.gross_in_cents;
      });
    }

    // Don't show $0.00 on the pages
    if (ferrySubtotalPrice === 0) {
      ferrySubtotalPrice = undefined;
    }
    if (vehiclePrice === 0) {
      vehiclePrice = undefined;
    }
    if (commissionPrice === 0) {
      commissionPrice = undefined;
    } else {
      ferrySubtotalPrice -= commissionPrice;
    }

    return {
      passengerPrices,
      ferrySubtotalPrice,
      vehiclePrice,
      commissionPrice,
    };
  }
);
